import { MaterialIcon } from '@/ui/material-icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/tooltip'
import type { AdpConnectionTestOptionField } from '@/features/adp/api/adpDataSources.types'
import type { DynamicSchemaField } from './ConfigureDataDestinationSheet.types'
import styles from './ConfigureDataDestinationSheet.module.scss'
import { DynamicConfigFormSkeleton } from '../shared/DynamicConfigFormSkeleton'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'
import { useState } from 'react'

type DynamicDestinationFieldsProps = {
  dynamicConfigFields: DynamicSchemaField[]
  form: Record<string, string>
  destinationName: string
  skipTestConnection?: boolean
  validationErrors: Record<string, string>
  showDynamicConfigSkeleton: boolean
  isTesting: boolean
  isSubmittingTarget: boolean
  hasRequiredTestSelections: boolean
  schemaTestFields: AdpConnectionTestOptionField[]
  responseSelections: Record<string, string>
  hasExcelSource?: boolean
  onChangeFormField: (key: string, value: string) => void
  onClearValidationError: (key: string) => void
  onChangeResponseSelection: (key: string, value: string) => void
  onTest: () => void
  onNext: () => void
}

const EMPTY_SELECT_VALUE = '__empty__'

function getResolvedInputType(isPasswordField: boolean, isPasswordVisible: boolean): 'text' | 'password' {
  if (!isPasswordField) return 'text'
  return isPasswordVisible ? 'text' : 'password'
}

type RenderDynamicFieldArgs = {
  field: DynamicSchemaField
  form: Record<string, string>
  destinationName: string
  validationErrors: Record<string, string>
  passwordVisibilityByKey: Record<string, boolean>
  onChangeFormField: (key: string, value: string) => void
  onClearValidationError: (key: string) => void
  onTogglePasswordVisibility: (key: string) => void
}

type RenderSelectControlArgs = {
  currentValue: string
  keyName: string
  label: string
  placeholder?: string
  options?: string[]
  error?: string
  onChangeFormField: (key: string, value: string) => void
  onClearValidationError: (key: string) => void
}

function renderSelectControl({
  currentValue,
  keyName,
  label,
  placeholder,
  options,
  error,
  onChangeFormField,
  onClearValidationError,
}: Readonly<RenderSelectControlArgs>): JSX.Element {
  return (
    <Select
      value={currentValue || EMPTY_SELECT_VALUE}
      onValueChange={value => {
        onChangeFormField(keyName, value === EMPTY_SELECT_VALUE ? '' : value)
        if (error) onClearValidationError(keyName)
      }}
    >
      <SelectTrigger className={`${styles.textInput} ${error ? styles.inputError : ''}`}>
        <SelectValue placeholder={placeholder || `Select ${label}`} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={EMPTY_SELECT_VALUE}>{placeholder || `Select ${label}`}</SelectItem>
        {(options ?? []).map(option => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

type RenderTextControlArgs = {
  isPasswordField: boolean
  isPasswordVisible: boolean
  inputType: 'text' | 'password'
  currentValue: string
  placeholder?: string
  error?: string
  keyName: string
  onChangeFormField: (key: string, value: string) => void
  onClearValidationError: (key: string) => void
  onTogglePasswordVisibility: (key: string) => void
}

function renderTextControl({
  isPasswordField,
  isPasswordVisible,
  inputType,
  currentValue,
  placeholder,
  error,
  keyName,
  onChangeFormField,
  onClearValidationError,
  onTogglePasswordVisibility,
}: Readonly<RenderTextControlArgs>): JSX.Element {
  return (
    <div className={isPasswordField ? styles.passwordInputWrap : undefined}>
      <Input
        className={`${styles.textInput} ${error ? styles.inputError : ''} ${isPasswordField ? styles.passwordTextInput : ''
          }`.trim()}
        type={inputType}
        value={currentValue}
        onChange={e => {
          onChangeFormField(keyName, e.target.value)
          if (error) onClearValidationError(keyName)
        }}
        placeholder={placeholder || 'Input Value'}
        autoComplete={isPasswordField ? 'new-password' : 'off'}
      />
      {isPasswordField && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={styles.passwordToggle}
          aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          onClick={() => onTogglePasswordVisibility(keyName)}
        >
          <MaterialIcon name={isPasswordVisible ? 'visibility_off' : 'visibility'} size={18} />
        </Button>
      )}
    </div>
  )
}

function renderDynamicField({
  field,
  form,
  destinationName,
  validationErrors,
  passwordVisibilityByKey,
  onChangeFormField,
  onClearValidationError,
  onTogglePasswordVisibility,
}: Readonly<RenderDynamicFieldArgs>): JSX.Element | null {
  const key = field.key ?? ''
  if (!key) return null
  const label = field.label ?? key
  const required = field.validators?.includes('required') ?? false
  const isPasswordField = field.type === 'password' || key.toLowerCase().includes('password')
  const isDestinationNameField = key === 'source_name' || key === 'destination_name'
  const currentValue = form[key] ?? (isDestinationNameField
    ? (form.destination_name ?? form.source_name ?? destinationName)
    : '')
  const error = validationErrors[key]
  const tooltipText = field.toolTip || `About ${label}`
  const isPasswordVisible = passwordVisibilityByKey[key] === true
  const inputType = getResolvedInputType(isPasswordField, isPasswordVisible)
  const isSelectField = field.type === 'select' || field.type === 'matselect'

  return (
    <label key={key} className={styles.field}>
      <span className={styles.fieldLabel}>
        {label}
        {required ? '' : ' (optional)'}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={styles.infoBtn} role="img" aria-label="Field info">
                <MaterialIcon name="info" size={14} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{tooltipText}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>

      {isSelectField
        ? renderSelectControl({
          currentValue,
          keyName: key,
          label,
          placeholder: field.placeholder,
          options: field.options,
          error,
          onChangeFormField,
          onClearValidationError,
        })
        : renderTextControl({
          isPasswordField,
          isPasswordVisible,
          inputType,
          currentValue,
          placeholder: field.placeholder,
          error,
          keyName: key,
          onChangeFormField,
          onClearValidationError,
          onTogglePasswordVisibility,
        })}

      {error && <span className={styles.fieldError}>{error}</span>}
    </label>
  )
}

export function DynamicDestinationFields({
  dynamicConfigFields,
  form,
  destinationName,
  skipTestConnection = false,
  validationErrors,
  showDynamicConfigSkeleton,
  isTesting,
  isSubmittingTarget,
  hasRequiredTestSelections,
  schemaTestFields,
  responseSelections,
  hasExcelSource = false,
  onChangeFormField,
  onClearValidationError,
  onChangeResponseSelection,
  onTest,
  onNext,
}: Readonly<DynamicDestinationFieldsProps>) {
  const defaultNextActionLabel = 'Save'
  const nextActionLabel = isSubmittingTarget ? 'Please wait...' : defaultNextActionLabel
  const [passwordVisibilityByKey, setPasswordVisibilityByKey] = useState<Record<string, boolean>>({})

  return (
    <>
      {showDynamicConfigSkeleton ? (
        <DynamicConfigFormSkeleton />
      ) : (
        dynamicConfigFields.map(field =>
          renderDynamicField({
            field,
            form,
            destinationName,
            validationErrors,
            passwordVisibilityByKey,
            onChangeFormField,
            onClearValidationError,
            onTogglePasswordVisibility: key =>
              setPasswordVisibilityByKey(prev => ({
                ...prev,
                [key]: !prev[key],
              })),
          }),
        )
      )}

      {schemaTestFields.map(field => (
        <label key={field.key} className={styles.field}>
          <span className={styles.fieldLabel}>
            {field.label}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={styles.infoBtn} role="img" aria-label="Field info">
                    <MaterialIcon name="info" size={14} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{field.toolTip || `About ${field.label}`}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
          <Select
            value={responseSelections[field.key] || EMPTY_SELECT_VALUE}
            onValueChange={value =>
              onChangeResponseSelection(field.key, value === EMPTY_SELECT_VALUE ? '' : value)
            }
          >
            <SelectTrigger className={styles.textInput}>
              <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EMPTY_SELECT_VALUE}>{field.placeholder || `Select ${field.label}`}</SelectItem>
              {(field.options ?? []).map(option => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ))}

      {!showDynamicConfigSkeleton && !skipTestConnection && schemaTestFields.length === 0 && (
        <div className={styles.actionsRow}>
          {hasExcelSource ? (
            <Button
              type="button"
              disabled={isSubmittingTarget}
              onClick={onNext}
            >
              {nextActionLabel}
            </Button>
          ) : (
            <Button type="button" onClick={onTest} disabled={isTesting}>
              {isTesting ? 'Testing...' : 'Test'}
            </Button>
          )}
        </div>
      )}

      {!showDynamicConfigSkeleton && (skipTestConnection || schemaTestFields.length > 0) && (
        <div className={styles.actionsRow}>
          <Button
            type="button"
            disabled={(skipTestConnection ? false : !hasRequiredTestSelections) || isSubmittingTarget}
            onClick={onNext}
          >
            {nextActionLabel}
          </Button>
        </div>
      )}
    </>
  )
}
